import React from "react";

import { useDecideFarmDetection, useGetFarmMainCropDetections, useTranslation } from "utils";

import { CropFCPO } from "models";

import { Button, Icon, Message, SkeletonWrapper, TagNormal, Typography } from "components";

import { COVER_CROP_CHIP, CoverCropChip, formatConfidence } from "pages/admin/verifications/utils/coverCropChips";
import {
  getCoverCropResultTag,
  getCoverCropSummary,
  getFarmerAnswer,
  getFarmerMainCrop,
  getMainCropClassLabel,
  getMainCropTone,
} from "pages/admin/verifications/utils/remoteSensingDetections";

type RemoteSensingDetectionsProps = {
  farmId: string;
  fieldId: string;
  seasonYear: number;
  chip: CoverCropChip;
  cropsOfYear: CropFCPO[];
};

const ANSWERS = [
  { isCorrect: true, label: "Yes", iconName: "tabler/check", selectedType: "primary" },
  { isCorrect: false, label: "No", iconName: "tabler/x", selectedType: "danger" },
] as const;

type FeedbackButtonsProps = {
  farmId: string;
  detectionType: "coverCrop" | "mainCrop";
  detectionId: string;
  savedAnswer: boolean | null;
};

// The verifier's feedback, kept for training the model. The saved answer shows as selected and can be switched.
const FeedbackButtons = ({ farmId, detectionType, detectionId, savedAnswer }: FeedbackButtonsProps) => {
  const { mutate, isPending, isSuccess, variables } = useDecideFarmDetection({ farmId, detectionType });
  const selected = (isPending || isSuccess) && variables ? variables.isCorrect : savedAnswer;
  const save = (isCorrect: boolean) =>
    isCorrect !== selected && mutate({ detectionId, isCorrect }, { onSuccess: () => Message({ icon: "tick", content: "Feedback saved" }) });

  return (
    <div className="satellite-detection-answer">
      {ANSWERS.map(({ isCorrect, label, iconName, selectedType }) => (
        <Button
          key={label}
          type={selected === isCorrect ? selectedType : "secondary"}
          size="small"
          iconLeft
          icon={<Icon iconName={iconName} size="xs" />}
          disabled={isPending}
          onClick={() => save(isCorrect)}
        >
          {label}
        </Button>
      ))}
    </div>
  );
};

// Loaded in the background by the crop list, so it is usually already there.
const MainCropRow = ({ farmId, fieldId, seasonYear, cropsOfYear }: Omit<RemoteSensingDetectionsProps, "chip">) => {
  const { t } = useTranslation(["practices"], { useSuspense: false });
  const { data, isLoading, isError, refetch } = useGetFarmMainCropDetections({ farmId });
  const detection = data?.find((row) => row.fieldId === fieldId && row.seasonYear === seasonYear);

  if (isLoading || isError) {
    return (
      <tr>
        <th scope="row">Main crop</th>
        <td colSpan={4}>
          {isError ? (
            <span className="satellite-detection-error">
              The main crop detection didn&apos;t load
              <Button type="secondary" size="small" onClick={() => refetch()}>
                Try again
              </Button>
            </span>
          ) : (
            <SkeletonWrapper isLoading type="block" height="1.5rem" />
          )}
        </td>
      </tr>
    );
  }
  if (!detection) return null;

  const farmerMainCrop = getFarmerMainCrop(cropsOfYear);
  const [predicted] = detection.rankedSuggestions;
  const classLabel = getMainCropClassLabel(detection.predictedClass);

  return (
    <tr className={getMainCropTone(detection, farmerMainCrop)}>
      <th scope="row">Main crop</th>
      <td>{farmerMainCrop?.cropName ?? "Not reported"}</td>
      <td>
        <span className="satellite-detected-value">
          {predicted ? (predicted.cropSlug ? t(`crops.${predicted.cropSlug}`) : predicted.cropName) : classLabel}
        </span>
        {predicted && <div className="satellite-detected-class">{classLabel}</div>}
      </td>
      <td>{detection.confidence === null ? "n/a" : formatConfidence(detection.confidence)}</td>
      <td>
        <FeedbackButtons farmId={farmId} detectionType="mainCrop" detectionId={detection.detectionId} savedAnswer={detection.verifierIsCorrect} />
      </td>
    </tr>
  );
};

// Shown only for years whose cover crop result made a chip on the crop timeline.
export const RemoteSensingDetections = ({ farmId, fieldId, seasonYear, chip, cropsOfYear }: RemoteSensingDetectionsProps) => {
  const tag = getCoverCropResultTag(chip);
  const summary = getCoverCropSummary(chip);
  const farmerCoverCrop = cropsOfYear.find(({ id }) => id === chip.cropId);

  return (
    <section className="satellite-detections">
      <div className="satellite-evidence-header">
        <Icon iconName="tabler/satellite" size="m" />
        <Typography variant="h5">Remote sensing · cover crop</Typography>
        <TagNormal
          className="satellite-evidence-badge"
          variant="paragraph-s"
          color={tag.color}
          text={
            <>
              <Icon iconName="tabler/satellite" size="s" className="icon inline" /> {tag.text}
            </>
          }
        />
      </div>
      <Typography variant="paragraph-s" color="grey-600">
        Check what the satellite detected. Say yes if the detected output is correct, no if it is not.
      </Typography>
      <table className="satellite-detection-table">
        <thead>
          <tr>
            <th scope="col">Detection</th>
            <th scope="col">Farmer reported</th>
            <th scope="col">What we detected</th>
            <th scope="col">Confidence</th>
            <th scope="col">Is the detected output correct?</th>
          </tr>
        </thead>
        <tbody>
          <MainCropRow farmId={farmId} fieldId={fieldId} seasonYear={seasonYear} cropsOfYear={cropsOfYear} />
          <tr className={chip.type === COVER_CROP_CHIP.CONFIRMED ? "success" : "danger"}>
            <th scope="row">Cover crop</th>
            <td>{farmerCoverCrop?.cropName ?? "No cover crop"}</td>
            <td>
              <span className="satellite-detected-value">{chip.type === COVER_CROP_CHIP.NOT_DETECTED ? "No cover crop" : "Cover crop"}</span>
            </td>
            <td>{formatConfidence(chip.confidence)}</td>
            <td>
              <FeedbackButtons
                farmId={farmId}
                detectionType="coverCrop"
                detectionId={chip.detection.detectionId}
                savedAnswer={chip.detection.verifierIsCorrect}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className={`satellite-detection-summary ${summary.tone}`}>
        <div className="satellite-detection-summary-headline">{summary.headline}</div>
        <div>
          Platform: {farmerCoverCrop?.cropName ?? "no cover crop"} · Satellite: {tag.text} · Farmer: {getFarmerAnswer(chip, cropsOfYear)}
        </div>
      </div>
    </section>
  );
};
